import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  message,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import type {
  CafeArticleImageShopView,
  PartnerShopExtractedShopMap,
  PartnerShopReviewPhoto,
} from '../PartnerCreatePage';
import {
  getPartnerShopSelectedReviewPhotoIds,
  getReviewPhotoCandidates,
  type ReviewPhotoCandidateItem,
} from '@/api/partnerShops';

const { Text, Title } = Typography;

interface ReviewPhotoSelectStepProps {
  // 수정 모달에서만 전달된다. 있으면 이미 저장된 사진 ID를 조회해 ON/OFF 초기값으로 쓴다.
  partnerShopId?: number;
  partnerShopExtractedShopMaps: PartnerShopExtractedShopMap[];

  partnerShopReviewPhotos: PartnerShopReviewPhoto[];
  onChangePartnerShopReviewPhotos: (
    nextPartnerShopReviewPhotos: PartnerShopReviewPhoto[]
  ) => void;

  cafeArticleImageShopViews: Record<string, CafeArticleImageShopView>;
  onChangeCafeArticleImageShopViews: (
    nextCafeArticleImageShopViews: Record<string, CafeArticleImageShopView>
  ) => void;
}

interface ReviewPhotoGroup {
  key: string;
  shopImageCategoryId: number | null;
  categoryName: string;
  photos: PartnerShopReviewPhoto[];
}

function toPositiveNumber(value: unknown) {
  const numberValue = Number(value);

  if (Number.isNaN(numberValue) || numberValue < 1) {
    return null;
  }

  return numberValue;
}

function getPartnerShopReviewPhotoKey(
  partnerShopReviewPhoto: PartnerShopReviewPhoto
) {
  if (partnerShopReviewPhoto.cafeArticleImageShopId) {
    return String(partnerShopReviewPhoto.cafeArticleImageShopId);
  }

  return `unknown-${partnerShopReviewPhoto.sortOrder}`;
}

function getCafeArticleImageShopViewKey(cafeArticleImageShopId: number | null) {
  if (!cafeArticleImageShopId) return '';
  return String(cafeArticleImageShopId);
}

function toPartnerShopReviewPhoto(
  item: ReviewPhotoCandidateItem,
  index: number,
  previousPartnerShopReviewPhoto?: PartnerShopReviewPhoto,
  selectedPhotoIds?: Set<number>
): PartnerShopReviewPhoto | null {
  const cafeArticleImageId = toPositiveNumber(item.cafeArticleImageId);
  const cafeArticleImageShopId = toPositiveNumber(item.cafeArticleImageShopId);

  if (!cafeArticleImageId || !cafeArticleImageShopId) {
    return null;
  }

  if (previousPartnerShopReviewPhoto) {
    return {
      ...previousPartnerShopReviewPhoto,
      cafeArticleImageId,
      cafeArticleImageShopId,
      shopImageCategoryId:
        previousPartnerShopReviewPhoto.shopImageCategoryId ??
        toPositiveNumber(item.categoryId),
      // 수정 모달에서 다시 불러올 때는 DB 기준 cafeArticleImageShopId 선택 상태를 우선한다.
      isPublished: selectedPhotoIds
        ? selectedPhotoIds.has(cafeArticleImageShopId)
          ? 1
          : 0
        : previousPartnerShopReviewPhoto.isPublished,
    };
  }

  return {
    cafeArticleImageId,
    cafeArticleImageShopId,
    shopImageCategoryId: toPositiveNumber(item.categoryId),

    caption: '',
    // create 화면은 전체 기본 선택, 수정 모달은 저장된 cafeArticleImageShopId만 선택한다.
    isPublished: selectedPhotoIds
      ? selectedPhotoIds.has(cafeArticleImageShopId)
        ? 1
        : 0
      : 1,
    sortOrder: index + 1,
    delYn: 0,
  };
}

function toCafeArticleImageShopView(
  item: ReviewPhotoCandidateItem
): CafeArticleImageShopView | null {
  const cafeArticleImageShopId = toPositiveNumber(item.cafeArticleImageShopId);

  if (!cafeArticleImageShopId) {
    return null;
  }

  return {
    cafeArticleImageShopId,
    cafeArticleImageId: toPositiveNumber(item.cafeArticleImageId),

    imageUrl: item.imageUrl || '',
    imageUrlW300: item.imageUrlW300,
    imageUrlW720: item.imageUrlW720,
    imageUrlW1080: item.imageUrlW1080,

    categoryName: item.categoryName || '기타',
    sourceUrl: item.sourceUrl,
    confidence: item.confidence,
  };
}

export default function ReviewPhotoSelectStep({
  partnerShopId,
  partnerShopExtractedShopMaps,
  partnerShopReviewPhotos,
  onChangePartnerShopReviewPhotos,
  cafeArticleImageShopViews,
  onChangeCafeArticleImageShopViews,
}: ReviewPhotoSelectStepProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(500);
  const [keyword, setKeyword] = useState<string>('');

  const sourceCafeExtractedShopIds = useMemo(() => {
    return partnerShopExtractedShopMaps
      .map((item) => Number(item.cafeExtractedShopId))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }, [partnerShopExtractedShopMaps]);

  const visiblePartnerShopReviewPhotos = useMemo(() => {
    return Array.isArray(partnerShopReviewPhotos)
      ? partnerShopReviewPhotos.filter((item) => Number(item.delYn || 0) === 0)
      : [];
  }, [partnerShopReviewPhotos]);

  const publishedCount = visiblePartnerShopReviewPhotos.filter(
    (photo) => Number(photo.isPublished) === 1
  ).length;

  const hiddenCount = visiblePartnerShopReviewPhotos.length - publishedCount;

  const filteredPartnerShopReviewPhotos = useMemo(() => {
    const trimmedKeyword = keyword.trim();

    if (!trimmedKeyword) {
      return visiblePartnerShopReviewPhotos;
    }

    return visiblePartnerShopReviewPhotos.filter((partnerShopReviewPhoto) => {
      const viewKey = getCafeArticleImageShopViewKey(
        partnerShopReviewPhoto.cafeArticleImageShopId
      );
      const view = cafeArticleImageShopViews[viewKey];

      return (
        view?.categoryName?.includes(trimmedKeyword) ||
        partnerShopReviewPhoto.caption?.includes(trimmedKeyword)
      );
    });
  }, [visiblePartnerShopReviewPhotos, cafeArticleImageShopViews, keyword]);

  const groupedPartnerShopReviewPhotos = useMemo<ReviewPhotoGroup[]>(() => {
    const groupMap = new Map<string, ReviewPhotoGroup>();

    filteredPartnerShopReviewPhotos.forEach((partnerShopReviewPhoto) => {
      const viewKey = getCafeArticleImageShopViewKey(
        partnerShopReviewPhoto.cafeArticleImageShopId
      );
      const view = cafeArticleImageShopViews[viewKey];

      const categoryName = view?.categoryName || '기타';
      const shopImageCategoryId =
        partnerShopReviewPhoto.shopImageCategoryId || null;
      const key = `${shopImageCategoryId || 'none'}-${categoryName}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          key,
          shopImageCategoryId,
          categoryName,
          photos: [],
        });
      }

      groupMap.get(key)?.photos.push(partnerShopReviewPhoto);
    });

    return Array.from(groupMap.values())
      .map((group) => ({
        ...group,
        photos: [...group.photos].sort(
          (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
        ),
      }))
      .sort((a, b) => {
        if (a.categoryName === '기타') return 1;
        if (b.categoryName === '기타') return -1;
        return a.categoryName.localeCompare(b.categoryName, 'ko');
      });
  }, [
    filteredPartnerShopReviewPhotos,
    cafeArticleImageShopViews,
  ]);

  const loadReviewPhotoCandidates = async () => {
    if (sourceCafeExtractedShopIds.length === 0) {
      message.warning('먼저 연결할 업체를 선택해 주세요.');
      return;
    }

    setLoading(true);

    try {
      const [result, selectedIdsResult] = await Promise.all([
        getReviewPhotoCandidates({
          cafeExtractedShopIds: sourceCafeExtractedShopIds,
          limit,
        }),
        // 수정 모달에서는 후보와 별도로 기존 저장 ID를 가져와 스위치 초기값에 반영한다.
        partnerShopId
          ? getPartnerShopSelectedReviewPhotoIds(partnerShopId)
          : Promise.resolve(null),
      ]);

      // reviewPhotoIds는 후보의 cafeArticleImageShopId와 같은 값이다.
      const selectedPhotoIds = selectedIdsResult
        ? new Set(
            (selectedIdsResult.reviewPhotoIds || []).map((id) => Number(id))
          )
        : undefined;

      const previousByCafeArticleImageShopId = new Map<
        number,
        PartnerShopReviewPhoto
      >();

      visiblePartnerShopReviewPhotos.forEach((partnerShopReviewPhoto) => {
        const cafeArticleImageShopId = toPositiveNumber(
          partnerShopReviewPhoto.cafeArticleImageShopId
        );

        if (cafeArticleImageShopId) {
          // 사용자가 이미 조정한 caption/publish 상태는 후보 재조회 때도 유지한다.
          previousByCafeArticleImageShopId.set(
            cafeArticleImageShopId,
            partnerShopReviewPhoto
          );
        }
      });

      const nextCafeArticleImageShopViews = {
        ...cafeArticleImageShopViews,
      };

      const nextPartnerShopReviewPhotos = result.list
        .map((item: ReviewPhotoCandidateItem, index: number) => {
          const cafeArticleImageShopId = toPositiveNumber(item.cafeArticleImageShopId);

          const previousPartnerShopReviewPhoto = cafeArticleImageShopId
            ? previousByCafeArticleImageShopId.get(cafeArticleImageShopId)
            : undefined;

          const nextPartnerShopReviewPhoto = toPartnerShopReviewPhoto(
            item,
            index,
            previousPartnerShopReviewPhoto,
            selectedPhotoIds
          );

          const cafeArticleImageShopView = toCafeArticleImageShopView(item);

          if (cafeArticleImageShopView) {
            nextCafeArticleImageShopViews[
              String(cafeArticleImageShopView.cafeArticleImageShopId)
            ] = cafeArticleImageShopView;
          }

          return nextPartnerShopReviewPhoto;
        })
        .filter(Boolean) as PartnerShopReviewPhoto[];

      onChangeCafeArticleImageShopViews(nextCafeArticleImageShopViews);
      onChangePartnerShopReviewPhotos(nextPartnerShopReviewPhotos);

      message.success(
        `후기 사진 ${nextPartnerShopReviewPhotos.length.toLocaleString()}장을 불러왔습니다.`
      );
    } catch (error) {
      console.error(
        '[ReviewPhotoSelectStep] failed to load review photo candidates',
        error
      );

      const errorMessage =
        error instanceof Error
          ? error.message
          : '후기 사진을 불러오지 못했습니다.';

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updatePartnerShopReviewPhoto = (
    partnerShopReviewPhotoKey: string,
    patch: Partial<PartnerShopReviewPhoto>
  ) => {
    onChangePartnerShopReviewPhotos(
      visiblePartnerShopReviewPhotos.map((partnerShopReviewPhoto) => {
        if (
          getPartnerShopReviewPhotoKey(partnerShopReviewPhoto) !==
          partnerShopReviewPhotoKey
        ) {
          return partnerShopReviewPhoto;
        }

        return {
          ...partnerShopReviewPhoto,
          ...patch,
        };
      })
    );
  };

  const updateAllPublished = (isPublished: number) => {
    onChangePartnerShopReviewPhotos(
      visiblePartnerShopReviewPhotos.map((partnerShopReviewPhoto) => ({
        ...partnerShopReviewPhoto,
        isPublished,
      }))
    );
  };

  const updateGroupPublished = (groupKey: string, isPublished: number) => {
    const targetGroup = groupedPartnerShopReviewPhotos.find(
      (group) => group.key === groupKey
    );

    if (!targetGroup) return;

    const targetKeySet = new Set(
      targetGroup.photos.map(getPartnerShopReviewPhotoKey)
    );

    onChangePartnerShopReviewPhotos(
      visiblePartnerShopReviewPhotos.map((partnerShopReviewPhoto) => {
        if (!targetKeySet.has(getPartnerShopReviewPhotoKey(partnerShopReviewPhoto))) {
          return partnerShopReviewPhoto;
        }

        return {
          ...partnerShopReviewPhoto,
          isPublished,
        };
      })
    );
  };

  const updateGroupSortOrder = (groupKey: string, startSortOrder: number) => {
    const targetGroup = groupedPartnerShopReviewPhotos.find(
      (group) => group.key === groupKey
    );

    if (!targetGroup) return;

    const targetKeySet = new Set(
      targetGroup.photos.map(getPartnerShopReviewPhotoKey)
    );

    let currentSortOrder = startSortOrder;

    onChangePartnerShopReviewPhotos(
      visiblePartnerShopReviewPhotos.map((partnerShopReviewPhoto) => {
        if (!targetKeySet.has(getPartnerShopReviewPhotoKey(partnerShopReviewPhoto))) {
          return partnerShopReviewPhoto;
        }

        const nextPhoto = {
          ...partnerShopReviewPhoto,
          sortOrder: currentSortOrder,
        };

        currentSortOrder += 1;

        return nextPhoto;
      })
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              후기 사진 선택
            </Title>
            <Text type="secondary">
              실제 후기에서 수집된 사진을 불러와 고객에게 보여줄 사진만 고릅니다.
              사진 원본 정보는 서버가 관리하고, 이 화면에서는 노출 여부와 설명만 정리합니다.
            </Text>
          </div>

          {sourceCafeExtractedShopIds.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="연결된 업체가 없습니다."
              description="먼저 첫 단계에서 업체를 선택해야 후기 사진을 불러올 수 있습니다."
            />
          )}

          <Row gutter={[16, 8]}>
            <Col>
              <Text strong>
                전체 {visiblePartnerShopReviewPhotos.length.toLocaleString()}장
              </Text>
            </Col>
            <Col>
              <Text strong type="success">
                노출 {publishedCount.toLocaleString()}장
              </Text>
            </Col>
            <Col>
              <Text type="secondary">
                숨김 {hiddenCount.toLocaleString()}장
              </Text>
            </Col>
          </Row>

          <Space wrap>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              disabled={sourceCafeExtractedShopIds.length === 0}
              onClick={loadReviewPhotoCandidates}
            >
              사진 불러오기
            </Button>

            <Button
              icon={<EyeOutlined />}
              disabled={visiblePartnerShopReviewPhotos.length === 0}
              onClick={() => updateAllPublished(1)}
            >
              전체 노출
            </Button>

            <Button
              icon={<EyeInvisibleOutlined />}
              disabled={visiblePartnerShopReviewPhotos.length === 0}
              onClick={() => updateAllPublished(0)}
            >
              전체 숨김
            </Button>

            <InputNumber
              min={1}
              max={2000}
              value={limit}
              addonBefore="불러올 수"
              addonAfter="장"
              onChange={(nextValue) => setLimit(Number(nextValue || 500))}
            />

            <Input.Search
              allowClear
              placeholder="카테고리 또는 설명 검색"
              style={{ width: 260 }}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </Space>
        </Space>
      </Card>

      {loading && (
        <Card>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">사진을 불러오는 중입니다.</Text>
            </div>
          </div>
        </Card>
      )}

      {!loading && visiblePartnerShopReviewPhotos.length === 0 && (
        <Card>
          <Empty description="아직 불러온 후기 사진이 없습니다.">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              disabled={sourceCafeExtractedShopIds.length === 0}
              onClick={loadReviewPhotoCandidates}
            >
              사진 불러오기
            </Button>
          </Empty>
        </Card>
      )}

      {!loading &&
        visiblePartnerShopReviewPhotos.length > 0 &&
        groupedPartnerShopReviewPhotos.length === 0 && (
          <Card>
            <Empty description="검색 조건에 맞는 사진이 없습니다." />
          </Card>
        )}

      {!loading &&
        groupedPartnerShopReviewPhotos.map((group) => {
          const groupPublishedCount = group.photos.filter(
            (photo) => Number(photo.isPublished) === 1
          ).length;

          const allGroupPublished =
            group.photos.length > 0 &&
            groupPublishedCount === group.photos.length;

          return (
            <Card
              key={group.key}
              title={
                <Space>
                  <Text strong>{group.categoryName}</Text>
                  <Tag>{group.photos.length.toLocaleString()}장</Tag>
                  <Tag color={groupPublishedCount > 0 ? 'blue' : 'default'}>
                    노출 {groupPublishedCount.toLocaleString()}장
                  </Tag>
                </Space>
              }
              extra={
                <Space wrap>
                  <Text type="secondary">그룹 전체 노출</Text>
                  <Switch
                    checked={allGroupPublished}
                    onChange={(checked) =>
                      updateGroupPublished(group.key, checked ? 1 : 0)
                    }
                  />

                  <InputNumber
                    min={0}
                    placeholder="정렬 시작"
                    style={{ width: 110 }}
                    onPressEnter={(event) => {
                      const target = event.target as HTMLInputElement;
                      updateGroupSortOrder(
                        group.key,
                        Number(target.value || 0)
                      );
                    }}
                  />
                </Space>
              }
            >
              <Row gutter={[12, 12]}>
                {group.photos.map((partnerShopReviewPhoto) => {
                  const partnerShopReviewPhotoKey =
                    getPartnerShopReviewPhotoKey(partnerShopReviewPhoto);

                  const viewKey = getCafeArticleImageShopViewKey(
                    partnerShopReviewPhoto.cafeArticleImageShopId
                  );
                  const cafeArticleImageShopView =
                    cafeArticleImageShopViews[viewKey];

                  const imageSrc =
                    cafeArticleImageShopView?.imageUrlW300 ||
                    cafeArticleImageShopView?.imageUrlW720 ||
                    cafeArticleImageShopView?.imageUrl ||
                    '';

                  return (
                    <Col key={partnerShopReviewPhotoKey} xs={12} md={8} lg={6} xl={4}>
                      <Card
                        size="small"
                        bodyStyle={{
                          padding: 8,
                          opacity:
                            Number(partnerShopReviewPhoto.isPublished) === 1
                              ? 1
                              : 0.45,
                        }}
                      >
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              aspectRatio: '1 / 1',
                              background: '#f5f5f5',
                              borderRadius: 8,
                              overflow: 'hidden',
                            }}
                          >
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={
                                  cafeArticleImageShopView?.categoryName ||
                                  '후기 사진'
                                }
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Text type="secondary">사진 없음</Text>
                              </div>
                            )}

                            <div
                              style={{
                                position: 'absolute',
                                right: 6,
                                top: 6,
                                background: 'rgba(255,255,255,0.9)',
                                borderRadius: 999,
                                padding: '2px 6px',
                              }}
                            >
                              <Switch
                                size="small"
                                checked={
                                  Number(partnerShopReviewPhoto.isPublished) === 1
                                }
                                onChange={(checked) =>
                                  updatePartnerShopReviewPhoto(
                                    partnerShopReviewPhotoKey,
                                    {
                                      isPublished: checked ? 1 : 0,
                                    }
                                  )
                                }
                              />
                            </div>
                          </div>

                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text style={{ fontSize: 12 }} ellipsis>
                              {cafeArticleImageShopView?.categoryName || '기타'}
                            </Text>

                            <Input
                              size="small"
                              placeholder="사진 설명"
                              value={partnerShopReviewPhoto.caption}
                              onChange={(event) =>
                                updatePartnerShopReviewPhoto(
                                  partnerShopReviewPhotoKey,
                                  {
                                    caption: event.target.value,
                                  }
                                )
                              }
                            />

                            <InputNumber
                              size="small"
                              min={0}
                              value={partnerShopReviewPhoto.sortOrder}
                              style={{ width: '100%' }}
                              addonBefore="정렬"
                              onChange={(nextValue) =>
                                updatePartnerShopReviewPhoto(
                                  partnerShopReviewPhotoKey,
                                  {
                                    sortOrder: Number(nextValue || 0),
                                  }
                                )
                              }
                            />
                          </Space>
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          );
        })}
    </Space>
  );
}
